import { useEffect, useState } from 'react'
import Modal from '../../componants/modal/Modal'
import service from '../../services/service'
import endpoints from '../../services/endpoints'
import { Train } from '../../assests/ExportImageIcon'

const formatRunningDays = (value) => {
    const days = (value || '').split(',').map((d) => d.trim()).filter(Boolean)
    if (days.length === 7) return 'All Days'
    return days.join(', ')
}

const formatLiveTime = (iso) => {
    if (!iso) return null
    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) return null
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })
}

// Minutes the actual time is after the scheduled one — null when either side
// hasn't happened yet, so "on time" isn't shown before the train even arrives.
const delayMinutes = (scheduled, actual) => {
    if (!scheduled || !actual) return null
    const schedMs = new Date(scheduled).getTime()
    const actualMs = new Date(actual).getTime()
    if (Number.isNaN(schedMs) || Number.isNaN(actualMs)) return null
    return Math.round((actualMs - schedMs) / 60000)
}

const delayLabel = (minutes) => {
    if (minutes > 0) return `Late by ${minutes} min`
    if (minutes < 0) return `Early by ${-minutes} min`
    return 'On time'
}

// /trains/train-list/'s route array only has station code/name/address — no
// per-stop arrival/departure/halt times — so the popup shows just those,
// styled as a timeline (not the fuller arrival/stop/departure table look),
// UNLESS "Track Live Status" is used: /trains/live/ returns a richer route
// (isHalt/status/platform/scheduled+actual times) for the same train number,
// which is normalized into the same {code,name} shape and swapped in below,
// with a 🚆 marker between the last departed stop and the next upcoming one.
const TrainRouteModal = ({ open, onClose, item, fromCode, toCode }) => {
    const [query, setQuery] = useState('')
    const [liveData, setLiveData] = useState(null)
    const [liveLoading, setLiveLoading] = useState(false)
    const [liveError, setLiveError] = useState(null)

    // Live status is per-train, not per-modal-open — clear it when a
    // different train's popup opens so stale data isn't shown briefly.
    useEffect(() => {
        setLiveData(null)
        setLiveError(null)
        setLiveLoading(false)
    }, [item?.train_number])

    if (!item) return null

    const fetchLiveStatus = async () => {
        if (liveLoading) return
        setLiveLoading(true)
        setLiveError(null)
        try {
            const { data } = await service.get(endpoints.trains.live, { train_number: item.train_number }, { silent: true })
            setLiveData(data.data)
        } catch {
            setLiveError('Unable to fetch live status right now.')
        } finally {
            setLiveLoading(false)
        }
    }

    const liveRoute = liveData?.route
    const route = liveRoute
        ? liveRoute
            .filter((stop) => stop.isHalt)
            .map((stop) => ({
                code: stop.stationCode,
                name: stop.stationName,
                distance: stop.distance,
                liveStatus: stop.status,
                platform: stop.platform,
                scheduledArrival: stop.scheduled_arrival,
                actualArrival: stop.actual_arrival,
                scheduledDeparture: stop.scheduled_departure,
                actualDeparture: stop.actual_departure,
                arrivalDelay: delayMinutes(stop.scheduled_arrival, stop.actual_arrival),
                departureDelay: delayMinutes(stop.scheduled_departure, stop.actual_departure),
            }))
        : (item.route || [])

    const filteredRoute = query.trim()
        ? route.filter((station) =>
            station.code.toLowerCase().includes(query.trim().toLowerCase()) ||
            station.name.toLowerCase().includes(query.trim().toLowerCase())
        )
        : route

    // Highlight only the searched from->to leg green — item.origin_code/
    // destination_code are the TRAIN's own full-run endpoints (e.g. a
    // NDLS-BJU train searched for just NDLS->LKO), not the searched leg, so
    // fromCode/toCode (passed down from the search bar) drive this instead.
    const originIndex = Math.max(route.findIndex((s) => s.code === (fromCode || item.origin_code)), 0)
    const foundDestinationIndex = route.findIndex((s) => s.code === (toCode || item.destination_code))
    const destinationIndex = foundDestinationIndex === -1 ? route.length - 1 : foundDestinationIndex

    return (
        <Modal open={open} onClose={onClose} maxWidth={520} className='trainRouteModal'>
            <div className='trainRouteModal__head'>
                <div className='trainRouteModal__title'>{item.train_number} - {item.train_name}</div>
                <div className='trainRouteModal__runsOn'>Runs on: {formatRunningDays(item.running_days)}</div>
            </div>

            <button type='button' className='trainRouteModal__liveBtn' onClick={fetchLiveStatus} disabled={liveLoading}>
                {liveLoading ? 'Fetching live status…' : liveData ? 'Refresh Live Status' : 'Track Live Status'}
            </button>
            {liveError && <p className='trainRouteModal__liveError'>{liveError}</p>}

            {liveData && (
                <div className='trainRouteModal__liveCard'>
                    <div className='trainRouteModal__liveStatusRow'>
                        <span className={`trainRouteModal__liveStatusBadge trainRouteModal__liveStatusBadge--${liveData.delay_minutes > 0 ? 'delayed' : 'ontime'}`}>
                            {liveData.delay_minutes > 0 ? `Delayed by ${liveData.delay_minutes} min` : 'On Time'}
                        </span>
                        {formatLiveTime(liveData.last_updated) && (
                            <span className='trainRouteModal__liveUpdated'>Updated {formatLiveTime(liveData.last_updated)}</span>
                        )}
                    </div>

                    <div className='trainRouteModal__liveProgressBar'>
                        <div className='trainRouteModal__liveProgressFill' style={{ width: `${liveData.overall_progress_pct || 0}%` }} />
                    </div>
                    <div className='trainRouteModal__liveProgressLabel'>{liveData.overall_progress_pct ?? 0}% of journey completed</div>

                    <div className='trainRouteModal__liveCurrent'>
                        Currently near <strong>{liveData.current_station_name}</strong> ({liveData.current_station_code})
                    </div>
                    {liveData.next_stop_name && (
                        <div className='trainRouteModal__liveNext'>
                            Next stop: <strong>{liveData.next_stop_name}</strong>{liveData.distance_to_next_halt_km ? ` — ${liveData.distance_to_next_halt_km} km away` : ''}
                        </div>
                    )}
                </div>
            )}

            <input
                type='text'
                className='trainRouteModal__search'
                placeholder='Search stations'
                value={query}
                onChange={(e) => setQuery(e.target.value)}
            />

            {!route.length ? (
                <p className='resultFlight__empty'>Route information not available for this train.</p>
            ) : (
                <div className='trainRouteModal__list'>
                    {filteredRoute.map((station, index) => {
                        const routeIndex = route.indexOf(station)
                        // In live mode, "active" means already crossed, not the searched leg.
                        const isDeparted = liveRoute
                            ? station.liveStatus === 'departed'
                            : routeIndex >= originIndex && routeIndex <= destinationIndex
                        // 🚆 marker sits right after the last departed stop, before the next upcoming one.
                        const nextStation = filteredRoute[index + 1]
                        const showTrainMarker = liveRoute && isDeparted && nextStation && nextStation.liveStatus !== 'departed'

                        return (
                            <div key={station.code + index}>
                                <div className={`trainRouteModal__stop ${isDeparted ? 'trainRouteModal__stop--active' : 'trainRouteModal__stop--passed'}`}>
                                    <div className='trainRouteModal__stopLine'>
                                        <span className='trainRouteModal__stopDot' />
                                        {(index !== filteredRoute.length - 1 || showTrainMarker) && <span className='trainRouteModal__stopConnector' />}
                                    </div>
                                    <div className='trainRouteModal__stopInfo'>
                                        <div className='trainRouteModal__stopHead'>
                                            <div className='trainRouteModal__stopNameCol'>
                                                <span className='trainRouteModal__stopCode'>{station.name}</span>
                                                <span className='trainRouteModal__stopName'>
                                                    {station.code}{station.distance != null ? ` • ${station.distance} km` : ''}{station.address ? ` — ${station.address}` : ''}
                                                </span>
                                            </div>

                                            {liveRoute && (
                                                <div className='trainRouteModal__stopLive'>
                                                    <span className={`trainRouteModal__stopLiveBadge trainRouteModal__stopLiveBadge--${isDeparted ? 'departed' : 'pending'}`}>
                                                        {isDeparted ? 'Departed' : 'Upcoming'}
                                                    </span>
                                                    {station.platform && <span className='trainRouteModal__stopPlatform'>Platform {station.platform}</span>}
                                                </div>
                                            )}
                                        </div>

                                        {/* Actual/delay only mean anything once the train has actually left this
                                            stop — for stations still ahead, show just the timetable time until
                                            a fresh "Refresh Live Status" reports it as departed. */}
                                        {liveRoute && formatLiveTime(station.scheduledArrival) && (
                                            <div className='trainRouteModal__stopTimesRow'>
                                                <span className='trainRouteModal__stopTimeItem trainRouteModal__stopTimeItem--start'>Arrival Sch {formatLiveTime(station.scheduledArrival)}</span>
                                                {isDeparted && station.arrivalDelay !== null && (
                                                    <span className={`trainRouteModal__stopTimeItem trainRouteModal__stopTimeItem--center ${station.arrivalDelay > 0 ? 'trainRouteModal__stopTimeItem--late' : 'trainRouteModal__stopTimeItem--ontime'}`}>
                                                        {delayLabel(station.arrivalDelay)}
                                                    </span>
                                                )}
                                                {isDeparted && formatLiveTime(station.actualArrival) && (
                                                    <span className='trainRouteModal__stopTimeItem trainRouteModal__stopTimeItem--end'>Actual {formatLiveTime(station.actualArrival)}</span>
                                                )}
                                            </div>
                                        )}
                                        {liveRoute && formatLiveTime(station.scheduledDeparture) && (
                                            <div className='trainRouteModal__stopTimesRow'>
                                                <span className='trainRouteModal__stopTimeItem trainRouteModal__stopTimeItem--start'>Departure Sch {formatLiveTime(station.scheduledDeparture)}</span>
                                                {isDeparted && station.departureDelay !== null && (
                                                    <span className={`trainRouteModal__stopTimeItem trainRouteModal__stopTimeItem--center ${station.departureDelay > 0 ? 'trainRouteModal__stopTimeItem--late' : 'trainRouteModal__stopTimeItem--ontime'}`}>
                                                        {delayLabel(station.departureDelay)}
                                                    </span>
                                                )}
                                                {isDeparted && formatLiveTime(station.actualDeparture) && (
                                                    <span className='trainRouteModal__stopTimeItem trainRouteModal__stopTimeItem--end'>Actual {formatLiveTime(station.actualDeparture)}</span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {showTrainMarker && (
                                    <div className='trainRouteModal__trainMarker'>
                                        <div className='trainRouteModal__stopLine'>
                                            <span className='trainRouteModal__trainMarkerDot'><img src={Train} alt='' /></span>
                                            <span className='trainRouteModal__stopConnector' />
                                        </div>
                                        <div className='trainRouteModal__trainMarkerInfo'>
                                            {liveData.current_station_name === nextStation.name
                                                ? <>Approaching <strong>{nextStation.name}</strong></>
                                                : <>Currently near <strong>{liveData.current_station_name}</strong> — heading to {nextStation.name}</>}
                                            <div className='trainRouteModal__trainMarkerProgressBar'>
                                                <div className='trainRouteModal__trainMarkerProgressFill' style={{ width: `${liveData.point_to_point_progress_pct || 0}%` }} />
                                            </div>
                                            <span className='trainRouteModal__trainMarkerProgressLabel'>{liveData.point_to_point_progress_pct ?? 0}% to next point</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            )}
        </Modal>
    )
}

export default TrainRouteModal
